import React from 'react';
import styles from './SliderMulti.module.scss';

export interface SliderMultiProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  id?: string;
  startValue: number;
  endValue: number;
  onChange: (startValue: number, endValue: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}

function SliderMulti({
  id,
  startValue,
  endValue,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  className,
  disabled,
  ...rest
}: SliderMultiProps): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement>(null);
  // const minRef = React.useRef(null);
  // const maxRef = React.useRef(null);
  const percentageString = (value: number) =>
    `${((value - min) / (max - min)) * 100}%`;

  const clampValue = (value: number) =>
    Math.min(max, Math.max(min, value));

  const snapValue = (value: number) => {
    const stepped = Math.round((value - min) / step) * step + min;
    return clampValue(Number(stepped.toFixed(5)));
  };

  const updateSlider = (start: number, end: number) => {
    if (!containerRef.current) return;
    if (start === startValue && end === endValue) return;
    if (start > end) {
      start = startValue;
      end = endValue;
    }
    containerRef.current.style.setProperty(
      '--range-start',
      percentageString(start),
    );
    containerRef.current.style.setProperty(
      '--range-end',
      percentageString(end),
    );
    onChange(start, end);
  };

  const handleTrackMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.target instanceof HTMLInputElement) return;
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const clickedValue = snapValue(min + ratio * (max - min));
    const startDistance = Math.abs(clickedValue - startValue);
    const endDistance = Math.abs(clickedValue - endValue);

    if (startDistance <= endDistance) {
      updateSlider(Math.min(clickedValue, endValue), endValue);
      return;
    }

    updateSlider(startValue, Math.max(clickedValue, startValue));
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container}${className ? ` ${className}` : ''}`}
      onMouseDown={handleTrackMouseDown}
      style={{
        '--range-start': percentageString(startValue),
        '--range-end': percentageString(endValue),
      } as React.CSSProperties}
    >
      <div className={styles.background}></div>

      <input
        {...rest}
        id={id ? `${id}-start` : undefined}
        type="range"
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={startValue}
        onChange={(e) => updateSlider(Number(e.target.value), endValue)}
      />
      <input
        {...rest}
        id={id ? `${id}-end` : undefined}
        type="range"
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={endValue}
        onChange={(e) => updateSlider(startValue, Number(e.target.value))}
      />
    </div>
  );
}

export default SliderMulti;
